import { useState, useEffect } from "react";
import { useNavigate } from "react-router";
import { toast } from "react-toastify";

import { DashboardLayout } from "../../Components/Layout/DashboardLayout";
import BackButton from "../../Components/BackButton";
import CustomInput from "../../Components/CustomInput";
import CustomButton from "../../Components/CustomButton";

import "./style.css";

const EMPTY_FORM = {
    current_password: "",
    password: "",
    password_confirmation: "",
};

const ChangePassword = () => {
    const navigate = useNavigate();
    const apiUrl = process.env.REACT_APP_BASE_URL;
    const token = localStorage.getItem("login");

    const [formData, setFormData] = useState(EMPTY_FORM);
    const [fieldErrors, setFieldErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        document.title = "Virtual PI | Change Password";
    }, []);

    const handleChanges = (event) => {
        const { name, value } = event.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
        if (fieldErrors[name]) {
            setFieldErrors((prev) => ({ ...prev, [name]: "" }));
        }
    };

    const validate = () => {
        const errs = {};

        if (!formData.current_password.trim()) {
            errs.current_password = "Current password is required.";
        }

        if (!formData.password.trim()) {
            errs.password = "New password is required.";
        } else if (formData.password.length < 8) {
            errs.password = "Password must be at least 8 characters.";
        }

        if (!formData.password_confirmation.trim()) {
            errs.password_confirmation = "Please confirm your new password.";
        } else if (formData.password !== formData.password_confirmation) {
            errs.password_confirmation = "Passwords do not match.";
        }

        return errs;
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (submitting) return;

        const errs = validate();
        if (Object.keys(errs).length > 0) {
            setFieldErrors(errs);
            return;
        }

        const body = new FormData();
        body.append("current_password", formData.current_password);
        body.append("password", formData.password);
        body.append("password_confirmation", formData.password_confirmation);

        setSubmitting(true);
        document.querySelector(".loaderBox")?.classList.remove("d-none");

        try {
            const response = await fetch(`${apiUrl}/change-password`, {
                method: "POST",
                headers: {
                    Accept: "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body,
            });

            const data = await response.json();

            if (data?.status === true) {
                toast.success(data.message || "Password changed successfully.");
                setFormData(EMPTY_FORM);
                setFieldErrors({});
                navigate("/profile");
            } else {
                toast.error(data?.message || "Failed to change password. Please try again.");
            }
        } catch (error) {
            console.error("Change password error:", error);
            toast.error("Something went wrong. Please check your connection and try again.");
        } finally {
            setSubmitting(false);
            document.querySelector(".loaderBox")?.classList.add("d-none");
        }
    };

    return (
        <DashboardLayout>
            <div className="dashCard mb-4">
                <div className="row mb-3">
                    <div className="col-12">
                        <h2 className="mainTitle">
                            <BackButton />
                            <span className="px-2">Change Password</span>
                        </h2>
                    </div>
                </div>

                <div className="row mb-3">
                    <div className="col-xl-4 col-lg-4">
                        <form onSubmit={handleSubmit} noValidate>

                            <div className="row mb-3">
                                <div className="col-12">
                                    <CustomInput
                                        label="Current Password"
                                        labelClass="mainLabel"
                                        required
                                        type="password"
                                        placeholder="Enter Current Password"
                                        name="current_password"
                                        inputClass="mainInput"
                                        onChange={handleChanges}
                                        value={formData.current_password}
                                    />
                                    {fieldErrors.current_password && (
                                        <p className="text-danger small mt-1">
                                            {fieldErrors.current_password}
                                        </p>
                                    )}
                                </div>
                            </div>

                            <div className="row mb-3">
                                <div className="col-12">
                                    <CustomInput
                                        label="New Password"
                                        labelClass="mainLabel"
                                        required
                                        type="password"
                                        name="password"
                                        placeholder="Enter New Password (min. 8 characters)"
                                        inputClass="mainInput"
                                        onChange={handleChanges}
                                        value={formData.password}
                                    />
                                    {fieldErrors.password && (
                                        <p className="text-danger small mt-1">
                                            {fieldErrors.password}
                                        </p>
                                    )}
                                </div>
                            </div>

                            <div className="row mb-3">
                                <div className="col-12">
                                    <CustomInput
                                        label="Confirm New Password"
                                        labelClass="mainLabel"
                                        required
                                        type="password"
                                        placeholder="Confirm New Password"
                                        inputClass="mainInput"
                                        name="password_confirmation"
                                        onChange={handleChanges}
                                        value={formData.password_confirmation}
                                    />
                                    {fieldErrors.password_confirmation && (
                                        <p className="text-danger small mt-1">
                                            {fieldErrors.password_confirmation}
                                        </p>
                                    )}
                                </div>
                            </div>

                            <div className="row mb-3">
                                <div className="col-12">
                                    <CustomButton
                                        variant="primaryButton"
                                        text={submitting ? "Updating..." : "Update"}
                                        type="submit"
                                        className="me-3 mb-2"
                                        disabled={submitting}
                                    />
                                </div>
                            </div>

                        </form>
                    </div>
                </div>
            </div>
        </DashboardLayout>
    );
};

export default ChangePassword;
